import { useCallback, useMemo } from 'react';
import authStorage from '../utils/authStorage';

const decodeJwt = (jwtToken) => {
  if (!jwtToken) return null;
  try {
    const parts = jwtToken.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const isTokenExpired = (jwtToken) => {
  const payload = decodeJwt(jwtToken);
  if (!payload || !payload.exp) return true;
  // Subtract a 5-second window to be safe against minor time sync discrepancies
  return payload.exp < (Date.now() / 1000) - 5;
};

export const useAuth = () => {
  const token = authStorage.getItem('token');
  const userJson = authStorage.getItem('user');
  
  const user = useMemo(() => {
    if (!userJson) return null;
    try {
      return JSON.parse(userJson);
    } catch (e) {
      return null;
    }
  }, [userJson]);
  
  const getPayload = useCallback(() => {
    return decodeJwt(token);
  }, [token]);

  const isAuthenticated = useMemo(() => {
    if (!token) return false;
    if (!isTokenExpired(token)) return true;
    
    // Fallback check to refresh token if access token is expired
    const refreshToken = authStorage.getItem('refreshToken');
    return !!refreshToken && !isTokenExpired(refreshToken);
  }, [token]);

  const roles = useMemo(() => {
    const payload = getPayload();
    if (!payload || !payload.roles) return [];
    return payload.roles.split(',').map(r => r.trim());
  }, [getPayload]);

  const permissions = useMemo(() => {
    const payload = getPayload();
    if (!payload || !payload.permissions) return [];
    return payload.permissions.split(',').map(p => p.trim());
  }, [getPayload]);

  const hasRole = useCallback((roleName) => {
    const norm = roleName.startsWith('ROLE_') ? roleName : `ROLE_${roleName.toUpperCase()}`;
    return roles.includes('ROLE_SUPER_ADMIN') || roles.includes(norm);
  }, [roles]);

  const hasPermission = useCallback((permissionName) => {
    return permissions.includes('SUPER_ADMIN') || permissions.includes('ROLE_SUPER_ADMIN') || permissions.includes(permissionName);
  }, [permissions]);

  const logout = useCallback(() => {
    authStorage.removeItem('token');
    authStorage.removeItem('refreshToken');
    authStorage.removeItem('user');
    window.location.href = '/login';
  }, []);

  const isAdmin = useCallback(() => {
    return roles.some(r => [
      'ROLE_SUPER_ADMIN', 
      'ROLE_SALES_ADMIN', 
      'ROLE_LISTING_ADMIN', 
      'ROLE_SUPPORT_ADMIN', 
      'ROLE_ADMIN',
      'ROLE_MAGAZINE_SALES'
    ].includes(r));
  }, [roles]);

  return useMemo(() => ({
    user,
    token,
    isAuthenticated,
    roles,
    permissions,
    hasRole,
    hasPermission,
    logout,
    isAdmin
  }), [user, token, isAuthenticated, roles, permissions, hasRole, hasPermission, logout, isAdmin]);
};

export default useAuth;
